import {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Turnstile, type TurnstileInstance} from '@marsidev/react-turnstile';
import {confirmForgotPassword, ensureTenantCookieFromQuery, forgotPassword} from '../services/authService';
import type {PasswordRequirements as PasswordRequirementsType} from '../utils/passwordValidation';
import {passwordsMatch, validatePassword} from '../utils/passwordValidation';
import PasswordRequirementsComponent from '../components/PasswordRequirements';
import {TURNSTILE_SITE_KEY} from '../config/env';
import './ConfirmForgotPassword.css';

/**
 * Parse the expiration-date param sent in the confirmation link.
 * The backend uses '*' as separator between date and time instead of 'T',
 * e.g. "2026-03-16*17:22:35.255792+00:00".
 * Returns a Date object; returns null if parsing fails.
 */
function parseExpirationDate(raw: string): Date | null {
  if (!raw) return null;
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})$/);
  if (!m) return null;

  const [, yS, moS, dS, hS, miS, sS] = m;
  const y = Number(yS);
  const mo = Number(moS) - 1; // Date month is 0-based
  const day = Number(dS);
  const h = Number(hS);
  const mi = Number(miS);
  const s = Number(sS);

  // Rango básico
  if (mo < 0 || mo > 11 || h < 0 || h > 23 || mi < 0 || mi > 59 || s < 0 || s > 59 || day < 1) {
    return null;
  }

  // Construir fecha en UTC y validar desbordes (ej. 2026-02-30)
  const dateUtc = new Date(Date.UTC(y, mo, day, h, mi, s, 0));
  if (
    dateUtc.getUTCFullYear() !== y ||
    dateUtc.getUTCMonth() !== mo ||
    dateUtc.getUTCDate() !== day ||
    dateUtc.getUTCHours() !== h ||
    dateUtc.getUTCMinutes() !== mi ||
    dateUtc.getUTCSeconds() !== s
  ) {
    return null;
  }

  // Restar 3 horas (GMT+0 → GMT-3)
  return new Date(dateUtc.getTime());
}

function ConfirmForgotPassword() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get('code') || '';
  const email = searchParams.get('email') || '';
  const rawExpiration = searchParams.get('expiration-date') ?? '';

  const expirationDate = parseExpirationDate(rawExpiration);
  const isExpired = !expirationDate || expirationDate <= new Date();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirementsType>({
    hasNumber: false,
    hasSpecialChar: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasMinLength: false,
    allRequirementsMet: false,
  });
  const [passwordMatch, setPasswordMatch] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState('');
  const [contextError, setContextError] = useState('');

  // Resend state (used when link is expired)
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileRef = useRef<TurnstileInstance>(null);

  const navigate = useNavigate();

  // Redirect to login 5 seconds after successful password reset
  useEffect(() => {
    if (!submitted) return;
    const timer = setTimeout(() => navigate('/login'), 5000);
    return () => clearTimeout(timer);
  }, [submitted, navigate]);

  // Ensure company context is available; create cookie from URL param if needed
  useEffect(() => {
    const tenant = ensureTenantCookieFromQuery(location.search);
    if (!tenant) {
      setContextError(
        'No se encontró el contexto de la empresa. Por favor abra el link desde el correo original o solicite uno nuevo.',
      );
    }
  // location.search is derived from URL params which do not change during the component lifetime.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    const requirements = validatePassword(value);
    setPasswordRequirements(requirements);
    setPasswordMatch(passwordsMatch(value, confirmPassword));
    if (errors.password) {
      setErrors((prev) => { const newErrors = { ...prev }; delete newErrors.password; return newErrors; });
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setPasswordMatch(passwordsMatch(password, value));
    if (errors.confirmPassword) {
      setErrors((prev) => { const newErrors = { ...prev }; delete newErrors.confirmPassword; return newErrors; });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!password.trim()) {
      newErrors.password = 'La contraseña es requerida';
    } else if (!passwordRequirements.allRequirementsMet) {
      newErrors.password = 'La contraseña no cumple con todos los requisitos de seguridad';
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = 'Debe confirmar la contraseña';
    } else if (!passwordMatch) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      await confirmForgotPassword({ code, email, password });
      setSubmitted(true);
    } catch (error) {
      console.error('Confirm forgot password error:', error);
      setApiError(error instanceof Error ? error.message : 'Ocurrió un error al restablecer la contraseña. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!turnstileToken) {
      setResendError('Por favor complete la verificación de seguridad');
      return;
    }
    setResending(true);
    setResendError('');
    setResendSuccess(false);
    try {
      await forgotPassword(email, turnstileToken);
      setResendSuccess(true);
      turnstileRef.current?.reset();
      setTurnstileToken('');
    } catch (err) {
      console.error('Resend error:', err);
      setResendError(err instanceof Error ? err.message : 'Error al reenviar el email. Por favor, intente nuevamente.');
      turnstileRef.current?.reset();
      setTurnstileToken('');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="confirm-forgot-password-container">
      <div className="confirm-forgot-password-box">
        <div className="confirm-forgot-password-header">
          <h1 className="confirm-forgot-password-title">Nueva Contraseña</h1>
          <p className="confirm-forgot-password-subtitle">
            {contextError
              ? 'Error de contexto'
              : submitted
              ? 'Su contraseña ha sido restablecida exitosamente'
              : isExpired
              ? 'El link de recuperación ha expirado'
              : 'Ingrese su nueva contraseña'}
          </p>
        </div>

        {contextError ? (
          <div className="error-state">
            <p className="error-message">{contextError}</p>
          </div>
        ) : submitted ? (
          <div className="success-message">
            <svg
              className="success-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p>Su contraseña ha sido restablecida. Será redirigido al inicio de sesión en 5 segundos...</p>
          </div>
        ) : isExpired ? (
          <div className="expired-state">
            <p className="expired-message">El link ya caducó. Por favor solicite uno nuevo.</p>

            {resendError && <p className="error-message">{resendError}</p>}
            {resendSuccess && <p className="resend-success-message">Se ha enviado un nuevo email con instrucciones.</p>}

            <div className="form-group turnstile-wrapper">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setTurnstileToken(token)}
                onError={() => setTurnstileToken('')}
                onExpire={() => setTurnstileToken('')}
              />
            </div>

            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="submit-button"
            >
              {resending ? 'Enviando...' : 'Enviar Instrucciones'}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="confirm-forgot-password-form">
            <div className="form-group">
              <label htmlFor="password">Nueva Contraseña</label>
              <div className="password-input-container">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Mínimo 8 caracteres"
                  disabled={loading}
                  autoComplete="new-password"
                  className={`form-input ${errors.password ? 'error' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle-button"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  disabled={loading}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.password && <span className="error-text">{errors.password}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar Contraseña</label>
              <div className="password-input-container">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  placeholder="Repita su contraseña"
                  disabled={loading}
                  autoComplete="new-password"
                  className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="password-toggle-button"
                  aria-label={showConfirmPassword ? 'Ocultar confirmar contraseña' : 'Mostrar confirmar contraseña'}
                  disabled={loading}
                >
                  {showConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
            </div>

            <div className="form-group">
              <PasswordRequirementsComponent
                requirements={passwordRequirements}
                passwordsMatch={passwordMatch}
                showMatchRequirement={true}
              />
            </div>

            {apiError && <p className="error-message">{apiError}</p>}

            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Guardando...' : 'Restablecer Contraseña'}
            </button>
          </form>
        )}

        <div className="confirm-forgot-password-footer">
          <button type="button" onClick={handleBackToLogin} className="back-button">
            ← Volver al inicio de sesión
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmForgotPassword;

